import { useCallback, useRef, useState } from 'react';

const OTP_LENGTH = 6;

// Inputs are gated at the DOM level via the `disabled` attribute on each input.
const useOtpInput = (onComplete) => {
  const [code, setCode] = useState(Array(OTP_LENGTH).fill(''));
  const inputRefs = useRef([]);

  const resetCode = useCallback(() => {
    setCode(Array(OTP_LENGTH).fill(''));
  }, []);

  const handleChange = useCallback(
    (index, value) => {
      // Only allow digits
      if (!/^\d*$/.test(value)) return;

      const newCode = [...code];
      newCode[index] = value.slice(-1); // Only take last character
      setCode(newCode);

      // Auto-focus next input
      if (value && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      // Auto-submit when complete
      if (newCode.every(Boolean)) {
        const fullCode = newCode.join('');
        if (onComplete) {
          onComplete(fullCode);
        }
      }
    },
    [code, onComplete],
  );

  const handleKeyDown = useCallback(
    (index, e) => {
      // Backspace to previous field
      if (e.key === 'Backspace' && !code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }

      // Arrow key navigation
      if (e.key === 'ArrowLeft' && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [code],
  );

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const digits = pastedText
        .replace(/\D/g, '')
        .slice(0, OTP_LENGTH)
        .split('');

      if (digits.length === 0) return;

      const newCode = Array(OTP_LENGTH).fill('');
      digits.forEach((digit, i) => {
        newCode[i] = digit;
      });
      setCode(newCode);

      // Focus the input after the last pasted digit, or the last input
      const focusIndex = Math.min(digits.length, OTP_LENGTH - 1);
      inputRefs.current[focusIndex]?.focus();

      // Auto-submit if all digits are filled
      if (digits.length === OTP_LENGTH && onComplete) {
        onComplete(newCode.join(''));
      }
    },
    [onComplete],
  );

  return {
    code,
    inputRefs,
    handleChange,
    handleKeyDown,
    handlePaste,
    resetCode,
  };
};

export default useOtpInput;
